import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL, headers } from '@/utils/apiConfig';

export interface OpenAIModel {
  value: string;
  label: string;
  supportsReasoning: boolean;
}

// Fallback models in case the API call fails
const FALLBACK_MODELS: OpenAIModel[] = [
  { value: 'gpt-5-nano-2025-08-07', label: 'GPT-5 Nano (Ultra Fast)', supportsReasoning: false },
  { value: 'gpt-5-mini-2025-08-07', label: 'GPT-5 Mini (Efficient)', supportsReasoning: false },
  { value: 'gpt-5-2025-08-07', label: 'GPT-5 (Flagship)', supportsReasoning: false },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Fast & Cheap)', supportsReasoning: false },
  { value: 'gpt-4o', label: 'GPT-4o (Balanced)', supportsReasoning: false },
  { value: 'gpt-4.1-mini-2025-04-14', label: 'GPT-4.1 Mini', supportsReasoning: false },
  { value: 'gpt-4.1-2025-04-14', label: 'GPT-4.1 (Advanced)', supportsReasoning: false },
  { value: 'o4-mini-2025-04-16', label: 'O4 Mini (Fast Reasoning)', supportsReasoning: true },
  { value: 'o3-2025-04-16', label: 'O3 (Deep Reasoning)', supportsReasoning: true },
];

async function fetchOpenAIModels(): Promise<OpenAIModel[]> {
  const response = await fetch(`${API_BASE_URL}/get-openai-models`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch models: ${response.status}`);
  }

  const data = await response.json();
  
  // If the response is an error object, throw it
  if (data.error) {
    throw new Error(data.error);
  }

  return data;
}

export function useOpenAIModels() {
  const query = useQuery({
    queryKey: ['openai-models'],
    queryFn: fetchOpenAIModels,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
    retry: 2,
    retryDelay: 1000,
  });

  return {
    models: query.data ?? FALLBACK_MODELS,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFallback: !query.data && !query.isLoading,
  };
}
